import { Slot } from '@radix-ui/react-slot';
import type { TooltipContentProps } from '@radix-ui/react-tooltip';
import { forwardRef, memo } from 'react';
import * as Tooltip from '~/components/ui/Tooltip';
import { classNames } from '~/utils/classNames';

export interface LegacyButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  variant: 'primary' | 'primary-light' | 'secondary' | 'warn' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  minHeight?: number | string;
  minWidth?: number | string;
  asChild?: true;
  title?: string;
  titleSide?: TooltipContentProps['side'];
  titleAlign?: TooltipContentProps['align'];
  titleSideOffset?: TooltipContentProps['sideOffset'];
  titleMaxWidth?: number;
  type?: 'button' | 'submit' | 'reset';
}

/**
 * @deprecated Use ~/components/ui/Button instead.
 */
export const LegacyButton = memo(
  forwardRef<HTMLButtonElement, LegacyButtonProps>(
    (
      {
        className,
        variant,
        size = 'md',
        minWidth,
        minHeight,
        disabled = false,
        title,
        titleSide = 'bottom',
        titleAlign = 'center',
        titleSideOffset = 6,
        titleMaxWidth,
        asChild,
        ...props
      },
      ref,
    ) => {
      const Comp = asChild ? Slot : 'button';

      return (
        <Tooltip.Provider>
          <Tooltip.Root delayDuration={300}>
            <Tooltip.Trigger asChild>
              <Comp
                ref={ref}
                className={classNames(
                  'flex rounded-md items-center font-medium justify-center outline-accent-600 [&:is(:disabled,.disabled)]:cursor-not-allowed [&:is(:disabled,.disabled)]:opacity-60',
                  size === 'lg' ? 'px-4 py-2' : 'px-3 py-1.5',
                  size === 'sm' ? 'text-xs' : 'text-sm',
                  {
                    disabled,
                    'bg-bolt-elements-button-primary-background text-bolt-elements-button-primary-text [&:not(:disabled,.disabled)]:hover:bg-bolt-elements-button-primary-backgroundHover outline-accent-500':
                      variant === 'primary',
                    'bg-bolt-elements-button-primaryLight-background text-bolt-elements-button-primaryLight-text [&:not(:disabled,.disabled)]:hover:bg-bolt-elements-button-primaryLight-backgroundHover outline-accent-500':
                      variant === 'primary-light',
                    'bg-bolt-elements-button-secondary-background text-bolt-elements-button-secondary-text [&:not(:disabled,.disabled)]:hover:bg-bolt-elements-button-secondary-backgroundHover':
                      variant === 'secondary',
                    'bg-bolt-elements-button-warn-background text-bolt-elements-button-warn-text [&:not(:disabled,.disabled)]:hover:bg-bolt-elements-button-warn-backgroundHover':
                      variant === 'warn',
                    'bg-bolt-elements-button-danger-background text-bolt-elements-button-danger-text [&:not(:disabled,.disabled)]:hover:bg-bolt-elements-button-danger-backgroundHover':
                      variant === 'danger',
                  },
                  className,
                )}
                style={{
                  ...(minWidth && { minWidth: typeof minWidth === 'string' ? minWidth : `${minWidth}px` }),
                  ...(minHeight && { minHeight: typeof minHeight === 'string' ? minHeight : `${minHeight}px` }),
                }}
                disabled={disabled}
                {...props}
              />
            </Tooltip.Trigger>
            {title && (
              <Tooltip.Portal>
                <Tooltip.Content
                  side={titleSide}
                  align={titleAlign}
                  sideOffset={titleSideOffset}
                  maxWidth={titleMaxWidth}
                >
                  {title}
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    },
  ),
);
